import { useCallback, useEffect, useRef, useState } from "react";
import EditIcon from "../../Icons/EditIcon";
import CloseIcon from "../../Icons/CloseIcon";
import CheckIcon from "../../Icons/CheckIcon";
import CrossIcon from "../../../Assets/images/cross.svg";
import dropdownStyles from "../../DropDown/PriceAdjustmentDropdowns/Style.module.css";
import sidebarStyles from "../rightsidebar.module.css";
import styles from "./CommissionInlineEdit.module.css";

export const validateCommissionInput = (raw, maxCommissionPrice, defaultCommission) => {
	const trimmed = String(raw ?? "").trim();
	if (!trimmed.replace(/^\$/, "")) return null;
	const normalized = trimmed.replace(/^\$/, "");
	if (/^\d+\.\d{3,}$/.test(normalized)) return "Only 2 decimal places are allowed.";
	if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return "Invalid commission format.";
	const num = parseFloat(normalized);
	if (Number.isNaN(num)) return "Invalid commission format.";
	if (num < 0) return "Commission cannot be negative.";
	const maxPrice = parseFloat(maxCommissionPrice);
	if (Number.isFinite(maxPrice) && num > maxPrice) return "Exceeds maximum commission.";
	const parsedDefault = parseCommissionInput(defaultCommission);
	if (Number.isFinite(parsedDefault) && parseCommissionInput(raw) === parsedDefault) {
		return "Commission is unchanged.";
	}
	return null;
};

export const parseCommissionInput = (raw) => {
	const trimmed = String(raw ?? "").trim().replace(/^\$/, "");
	return Math.round(parseFloat(trimmed) * 100) / 100;
};

const isDraftBlank = (raw) => {
	const normalized = String(raw ?? "").trim().replace(/^\$/, "");
	return normalized === "";
};

const CommissionInlineEdit = ({
	value,
	maxCommissionPrice,
	defaultCommission,
	isEdited = false,
	onSave,
	onReload,
	disabled = false,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draftValue, setDraftValue] = useState("");
	const [error, setError] = useState("");
	const containerRef = useRef(null);
	const inputRef = useRef(null);

	const formattedValue = `$${Number(value || 0).toFixed(2)}`;

	const startEditing = useCallback(() => {
		if (disabled) return;
		setDraftValue(formattedValue);
		setError("");
		setIsEditing(true);
	}, [disabled, formattedValue]);

	const cancelEditing = useCallback(() => {
		setIsEditing(false);
		setDraftValue("");
		setError("");
	}, []);

	const commitSave = useCallback(() => {
		if (isDraftBlank(draftValue)) return false;
		const validationError = validateCommissionInput(draftValue, maxCommissionPrice, defaultCommission);
		if (validationError) {
			setError(validationError);
			return false;
		}
		const parsed = parseCommissionInput(draftValue);
		onSave?.(parsed);
		setIsEditing(false);
		setDraftValue("");
		setError("");
		return true;
	}, [draftValue, maxCommissionPrice, defaultCommission, onSave]);

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isEditing) return undefined;

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				cancelEditing();
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isEditing, cancelEditing]);

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			if (!isDraftBlank(draftValue)) {
				commitSave();
			}
		}
		if (event.key === "Escape") {
			event.preventDefault();
			cancelEditing();
		}
	};

	const canSave = !isDraftBlank(draftValue);

	if (!isEditing) {
		return (
			<div className={styles.wrapper}>
				<div className={styles.readOnlyRow}>
					<div className={styles.readOnlyLeft}>
						<label className={sidebarStyles.label}>Commission:</label>
						<p className={styles.value}>{formattedValue}</p>
						{isEdited ? (
							<div className={dropdownStyles.selectedDiscount}>
								<span className={dropdownStyles.selectedDiscountText}>
									Manual override
								</span>
								<button
									type="button"
									className={dropdownStyles.selectedDiscountClose}
									onClick={() => onReload?.()}
									disabled={disabled}
									title="Original commission will be restored"
									aria-label="Remove manual commission override">
									<img src={CrossIcon} alt="" width={10} height={10} />
								</button>
							</div>
						) : (
							<button
								type="button"
								className={styles.editBtn}
								onClick={startEditing}
								disabled={disabled}
								title="Edit commission"
								aria-label="Edit commission">
								<EditIcon size={12} color="#3C2212" />
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper} ref={containerRef}>
			<div className={styles.readOnlyRow}>
				<label className={sidebarStyles.label}>Commission:</label>
				<div className={styles.editGroup}>
					<div className={styles.editRow}>
						<div className={styles.inputWrapper}>
							<button
								type="button"
								className={styles.cancelBtn}
								onClick={cancelEditing}
								title="Cancel edit"
								aria-label="Cancel edit">
								<CloseIcon size={11} />
							</button>
							<input
								ref={inputRef}
								type="text"
								className={`${styles.input} ${error ? styles.inputError : ""}`}
								value={draftValue}
								onChange={(event) => {
									setDraftValue(event.target.value);
									if (error) setError("");
								}}
								onKeyDown={handleKeyDown}
							/>
						</div>
						<button
							type="button"
							className={styles.saveBtn}
							onClick={commitSave}
							disabled={!canSave}
							title="Save changes"
							aria-label="Save changes">
							<CheckIcon width={15} height={11} />
						</button>
					</div>
					{error ? <p className={styles.errorMessage}>{error}</p> : null}
				</div>
			</div>
		</div>
	);
};

export default CommissionInlineEdit;
